import { mailNotifySuiteAuthError } from "@/auth/suiteAuthBoot";
import { getMailApiBase } from "@/auth/paths";
import { mailAuthHeaders } from "@/auth/session";

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; message: string; error?: string };

async function parseJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

function handleAuthError(status: number, error?: string): void {
  mailNotifySuiteAuthError(status, error);
}

function networkErrorMessage(err: unknown): string {
  if (err instanceof TypeError) {
    return "Connexion indisponible — vérifiez le réseau ou réessayez dans un instant.";
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return "Connexion indisponible.";
}

export async function mailApiGet<T>(
  path: string,
  options?: { timeoutMs?: number }
): Promise<ApiResult<T>> {
  const url = `${getMailApiBase()}/${path.replace(/^\//u, "")}`;
  const controller = new AbortController();
  const timeoutMs = options?.timeoutMs;
  const timer =
    timeoutMs != null && timeoutMs > 0
      ? window.setTimeout(() => controller.abort(), timeoutMs)
      : null;

  let res: Response;
  try {
    res = await fetch(url, {
      credentials: "include",
      headers: { Accept: "application/json", ...mailAuthHeaders() },
      signal: timeoutMs != null ? controller.signal : undefined,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return {
        ok: false,
        status: 0,
        message: "Délai dépassé — le serveur met trop de temps à répondre.",
      };
    }
    return { ok: false, status: 0, message: networkErrorMessage(err) };
  } finally {
    if (timer != null) window.clearTimeout(timer);
  }
  const body = (await parseJson(res)) as T & { success?: boolean; message?: string; error?: string };
  if (!res.ok || body.success === false) {
    handleAuthError(res.status, body.error);
    return {
      ok: false,
      status: res.status,
      message: body.message || `Erreur HTTP ${res.status}`,
      error: body.error,
    };
  }
  return { ok: true, data: body };
}

export async function mailApiPost<T>(
  path: string,
  payload: unknown,
  options?: { timeoutMs?: number }
): Promise<ApiResult<T>> {
  const url = `${getMailApiBase()}/${path.replace(/^\//u, "")}`;
  const controller = new AbortController();
  const timeoutMs = options?.timeoutMs ?? 45_000;
  const timer =
    timeoutMs > 0 ? window.setTimeout(() => controller.abort(), timeoutMs) : null;

  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...mailAuthHeaders(),
      },
      body: JSON.stringify(payload),
      signal: timeoutMs > 0 ? controller.signal : undefined,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return {
        ok: false,
        status: 0,
        message: "Délai dépassé — le serveur met trop de temps à répondre.",
      };
    }
    return { ok: false, status: 0, message: networkErrorMessage(err) };
  } finally {
    if (timer != null) window.clearTimeout(timer);
  }
  const body = (await parseJson(res)) as T & { success?: boolean; message?: string; error?: string };
  if (!res.ok || body.success === false) {
    handleAuthError(res.status, body.error);
    return {
      ok: false,
      status: res.status,
      message: body.message || `Erreur HTTP ${res.status}`,
      error: body.error,
    };
  }
  return { ok: true, data: body };
}
